import { Injectable } from '@angular/core';
import { BusinessSummary } from 'src/app/types/BusinessSummary';
import { PaymentMethodDetails } from 'src/app/types/PaymentDetails';
import { environment } from '../../../../environments/environment';
import {
  BankDetails,
  BeneficialOwner,
  Currency,
  InvoicesModel,
  Money,
  OwnerAddress,
  PartnerAccountDetails,
  PartnerModel,
  PatchBeneficialOwnerRequestContent,
  PatchOwnerOperation,
  PostBeneficialOwnerRequestContent,
  PostCurrencyWalletResponseContent,
  PostMultiCurrencyEnableResponseContent,
} from '../../../api/autogenerated';
import {
  buildWalletBalancesByCurrency,
  getAccountBalanceForCurrency,
  getWalletBalanceForCurrency,
  shouldUseVendorWalletBalance,
  shouldUseWalletBalanceListForTopLevel,
} from '../../../shared/utils/account-balance.util';
import { Business } from '../../../types/Business';
import { BusinessSummaryRequestType } from '../../../types/BusinessSummaryRequestType';
import { Invoice } from '../../../types/Invoice';
import {
  InvoiceActivity,
  InvoiceActivityType,
} from '../../../types/InvoiceActivity';
import { InvoiceGroup } from '../../../types/InvoiceGroup';
import { InvoiceStatus } from '../../../types/InvoiceStatus';
import { Profile } from '../../../types/Profile';
import { BusinessSummaryRequest } from '../../../types/requests/BusinessSummaryRequest';
import { TopLevelNumbers } from '../../../types/TopLevelNumbers';
import { SharedProfileData } from '../../data/shared-profile-data';
import { IBusinessSummaryService } from '../IBusinessSummaryService';
import { mockBusinessSummary } from '../mock/mock-data';
import { OpenApiService } from '../open-api.service';

@Injectable({
  providedIn: 'root',
})
export class BusinessSummaryService implements IBusinessSummaryService {
  options = {
    headers: {
      'X-Organization-ID': environment.organization_id,
      customer_id: '',
    },
  };

  constructor(
    private sharedProfileData: SharedProfileData,
    private openapiService: OpenApiService,
  ) {}

  leaveBusiness(businessId: string): Promise<BusinessSummary> {
    let remaining = mockBusinessSummary.businesses.filter(
      (business) => business.r2AId === businessId,
    );
    return Promise.resolve({
      ...mockBusinessSummary,
      businesses: remaining,
    });
  }

  private getInvoiceStatus(invoice: InvoicesModel): InvoiceStatus {
    switch (invoice.status) {
      case 'DRAFT':
        return InvoiceStatus.Draft;
      case 'UNPAID':
        return InvoiceStatus.Unpaid;
      case 'PAID':
        return InvoiceStatus.Paid;
      case 'OVERDUE':
        return InvoiceStatus.Overdue;
      case 'PARTIALLY_PAID':
        return InvoiceStatus.PartialPaid;
      case 'REJECTED':
        return InvoiceStatus.Rejected;
      case 'VERIFYING':
        return InvoiceStatus.Verifying;
      case 'CANCELLED':
        return InvoiceStatus.Revoked;
      default:
        return InvoiceStatus.Canceled;
    }
  }

  //
  // private async getPartnerDetailsForInvoice(): Promise<{[key: string]: Business}> {
  //   const allPartners = await this.getAllClientBusinesses();
  //   let partners : {[key: string]: Business} = {}
  //
  //   allPartners.forEach((partner: Business) => {
  //     if (partner.partnerId !== undefined) {
  //       partners[partner.partnerId] = {
  //         name: partner.name,
  //         country: partner.country,
  //         clientName: partner.clientName,
  //         profileImagePath: partner.profileImagePath
  //       }
  //     }
  //   })
  //   return partners ? partners : {} as {[key: string]: Business};
  // }

  private async convertInvoicesFromApiToFrontend(
    invoices: InvoicesModel[],
    partners: Business[],
  ): Promise<Invoice[]> {
    let invoiceList: Invoice[] = [];
    // extract partnerIds from invoices and get all partners

    invoices.forEach((invoice) => {
      const partner = partners.find(
        (partner) => partner.partnerId == invoice.partner_id,
      );

      invoiceList.push({
        id: invoice.id,
        billingStartDateUTC: invoice.billing_start_date,
        billingEndDateUTC: invoice.billing_end_date,
        invoiceDate: invoice.invoice_date,
        dueDate: invoice.invoice_due_date,
        totalAmount: {
          value: invoice.total,
          currency: invoice.currency,
        },
        dueAmount: {
          value: invoice.due_amount,
          currency: invoice.currency,
        },
        partnerCurrentBalance: partner?.currentBalance,
        status: this.getInvoiceStatus(invoice),
        purposeOfTransaction: invoice.purpose_of_transaction,
        isValid: false,
        partnerId: invoice.partner_id,
        invoiceNote: invoice.purpose_of_transaction,
        invoiceNo: invoice.invoice_number,
        invoiceDocument: invoice.invoice_document,
        billFromAddress: '',
        billToAddress: '',
        billFrom: '',
        vat: invoice.vat,
        purposeCode: invoice.purpose_code,
        subTotal: invoice.sub_total,
        billTo:
          partners.find((partner) => partner.partnerId == invoice.partner_id)
            ?.name ?? '',
        profilePath:
          partners.find((partner) => partner.partnerId == invoice.partner_id)
            ?.profileImagePath ?? '',
        transactionType: invoice.transaction_type,
        invoiceLog: {
          invoiceNo: invoice.invoice_number,
          name: partner?.contactPerson ?? '',
          bankDetails: partner?.bankDetails ?? [],
          companyName: partner?.name ?? '',
          emailAddress: partner?.emailAddress ?? '',
          amount: {
            value: invoice.total,
            currency: invoice.currency,
          },
          issueDate: invoice.invoice_date,
          dueDate: invoice.invoice_due_date,
          timeline: this.getInvoiceTimelineEvents(invoice),
        },
      });
    });
    return invoiceList;
  }

  private getInvoiceTimelineEvents(invoice: InvoicesModel) {
    let timelineEvents: InvoiceActivity[] = [];
    invoice.timeline?.forEach((event) => {
      timelineEvents.push({
        aTitle: event.title,
        aAnnotation: event.message,
        aType: event.type as InvoiceActivityType,
        aDate: new Date(event.time),
      });
    });
    return timelineEvents.sort((a, b) => b.aDate.getTime() - a.aDate.getTime());
  }

  private async getSentInvoicesInformation(): Promise<InvoicesModel[]> {
    const customer: Profile = this.sharedProfileData.getUserProfile();

    let dataFromResponse = await this.openapiService.getInvoices(
      customer.businessId,
      {
        partner_ids: [],
      },
    );

    return dataFromResponse.data;
  }

  private async getTopLevelNumbers(
    businesses: Business[],
    invoiceGroups: InvoiceGroup[],
  ): Promise<TopLevelNumbers> {
    let data: TopLevelNumbers = {
      activeCustomers: 0,
      totalDues: 0,
      totalInvoicesDue: 0,
      totalInvoicesRaised: 0,
      totalRaisedInvoices: 0,
      totalRevenuesEarned: 0,
      usdAccountBalance: 0,
      totalInvoicesRaisedCount: 0,
      totalDuesCount: 0,
      totalRevenuesEarnedCount: 0,
    };

    const profileForBalance = this.sharedProfileData.getUserProfile();

    if (shouldUseWalletBalanceListForTopLevel(profileForBalance)) {
      let walletBalances = profileForBalance?.walletBalancesByCurrency;
      if (!walletBalances || Object.keys(walletBalances).length === 0) {
        walletBalances = await this.getVendorWalletBalancesFromApi();
      }
      data.usdAccountBalance = getWalletBalanceForCurrency(
        walletBalances,
        'USD',
      );
    } else if (shouldUseVendorWalletBalance(businesses)) {
      const walletBalances = await this.getVendorWalletBalancesFromApi();
      data.usdAccountBalance = getWalletBalanceForCurrency(
        walletBalances,
        'USD',
      );
    } else {
      data.usdAccountBalance = getAccountBalanceForCurrency(businesses, 'USD');
    }

    let activeCustomers: string[] = [];
    invoiceGroups.forEach((invoiceGroup: InvoiceGroup) => {
      if (invoiceGroup.groupName == 'All') {
        return;
      }

      invoiceGroup.invoices.forEach((invoice) => {
        if (
          [
            InvoiceStatus.Unpaid,
            InvoiceStatus.Paid,
            InvoiceStatus.PartialPaid,
            InvoiceStatus.Verifying,
            InvoiceStatus.Overdue,
          ].indexOf(invoice.status) > -1
        ) {
          data.totalInvoicesRaised += invoice.totalAmount.value;
          data.totalInvoicesRaisedCount +=
            invoice.totalAmount.value > 0 ? 1 : 0;
          invoice.partnerId && activeCustomers.indexOf(invoice.partnerId) == -1
            ? activeCustomers.push(invoice.partnerId)
            : null;
          data.totalRevenuesEarned +=
            invoice.totalAmount.value - invoice.dueAmount.value;
          //data.totalInvoicesRaisedCount += (invoice.totalAmount.value - invoice.dueAmount.value > 0) ? 1 : 0;
          data.totalDues += invoice.dueAmount.value;
          data.totalDuesCount += invoice.dueAmount.value > 0 ? 1 : 0;
        }
      });
    });
    data.activeCustomers = activeCustomers.length;

    return data;
  }

  private async getVendorWalletBalancesFromApi(): Promise<
    Record<string, number>
  > {
    const profile = this.sharedProfileData.getUserProfile();
    const walletBalancesFromProfile = profile?.walletBalancesByCurrency;
    if (walletBalancesFromProfile) {
      return walletBalancesFromProfile;
    }

    if (!profile?.businessId) {
      return {};
    }

    const response = await this.openapiService.getCustomerById({
      id: profile.businessId,
    });

    if (!response.success || !response.data) {
      return {};
    }

    return buildWalletBalancesByCurrency(response.data.wallet_balance_list);
  }

  private convertSentInvoicesFromApiToFrontend(
    invoices: Invoice[],
  ): InvoiceGroup[] {
    // Initialize groups with proper structure
    let customInvoiceGroups: { [key: string]: InvoiceGroup } = {
      All: {
        groupName: 'All',
        invoices: [],
        topLevelNumbers: {
          usdAccountBalance: 0,
          totalInvoicesRaised: 0,
          totalRaisedInvoices: 0,
          totalDues: 0,
          totalInvoicesDue: 0,
          totalRevenuesEarned: 0,
          activeCustomers: 0,
          totalInvoicesRaisedCount: 0,
          totalDuesCount: 0,
          totalRevenuesEarnedCount: 0,
        },
      },
      Active: {
        groupName: 'Active',
        invoices: [],
        topLevelNumbers: {
          usdAccountBalance: 0,
          totalInvoicesRaised: 0,
          totalRaisedInvoices: 0,
          totalDues: 0,
          totalInvoicesDue: 0,
          totalRevenuesEarned: 0,
          activeCustomers: 0,
          totalInvoicesRaisedCount: 0,
          totalDuesCount: 0,
          totalRevenuesEarnedCount: 0,
        },
      },
      Draft: {
        groupName: 'Draft',
        invoices: [],
        topLevelNumbers: {
          usdAccountBalance: 0,
          totalInvoicesRaised: 0,
          totalRaisedInvoices: 0,
          totalDues: 0,
          totalInvoicesDue: 0,
          totalRevenuesEarned: 0,
          activeCustomers: 0,
          totalInvoicesRaisedCount: 0,
          totalDuesCount: 0,
          totalRevenuesEarnedCount: 0,
        },
      },
      Revoked: {
        groupName: 'Revoked',
        invoices: [],
        topLevelNumbers: {
          usdAccountBalance: 0,
          totalInvoicesRaised: 0,
          totalRaisedInvoices: 0,
          totalDues: 0,
          totalInvoicesDue: 0,
          totalRevenuesEarned: 0,
          activeCustomers: 0,
          totalInvoicesRaisedCount: 0,
          totalDuesCount: 0,
          totalRevenuesEarnedCount: 0,
        },
      },
      Paid: {
        groupName: 'Paid',
        invoices: [],
        topLevelNumbers: {
          usdAccountBalance: 0,
          totalInvoicesRaised: 0,
          totalRaisedInvoices: 0,
          totalDues: 0,
          totalInvoicesDue: 0,
          totalRevenuesEarned: 0,
          activeCustomers: 0,
          totalInvoicesRaisedCount: 0,
          totalDuesCount: 0,
          totalRevenuesEarnedCount: 0,
        },
      },
    };

    invoices.forEach((invoice) => {
      // Add to All group
      customInvoiceGroups['All'].invoices.push(invoice);

      // Categorize based on status
      if (invoice.status === InvoiceStatus.Draft) {
        customInvoiceGroups['Draft'].invoices.push(invoice);
      } else if (invoice.status === InvoiceStatus.Revoked) {
        customInvoiceGroups['Revoked'].invoices.push(invoice);
      } else if (invoice.status === InvoiceStatus.Paid) {
        customInvoiceGroups['Paid'].invoices.push(invoice);
      } else if (
        invoice.status === InvoiceStatus.Unpaid ||
        invoice.status === InvoiceStatus.PartialPaid ||
        invoice.status === InvoiceStatus.Overdue ||
        invoice.status === InvoiceStatus.Verifying
      ) {
        customInvoiceGroups['Active'].invoices.push(invoice);
      }

      // Calculate top level numbers for All group
      if (
        [
          InvoiceStatus.Unpaid,
          InvoiceStatus.Paid,
          InvoiceStatus.PartialPaid,
          InvoiceStatus.Verifying,
          InvoiceStatus.Overdue,
        ].indexOf(invoice.status) > -1
      ) {
        customInvoiceGroups['All'].topLevelNumbers.totalInvoicesRaised +=
          invoice.totalAmount.value;
        customInvoiceGroups['All'].topLevelNumbers.totalDues +=
          invoice.dueAmount.value;
        customInvoiceGroups['All'].topLevelNumbers.totalRaisedInvoices =
          customInvoiceGroups['All'].topLevelNumbers.totalInvoicesRaised -
          customInvoiceGroups['All'].topLevelNumbers.totalDues;
      }

      // Calculate top level numbers for Active group
      if (
        [
          InvoiceStatus.Unpaid,
          InvoiceStatus.PartialPaid,
          InvoiceStatus.Overdue,
          InvoiceStatus.Verifying,
        ].indexOf(invoice.status) > -1
      ) {
        customInvoiceGroups['Active'].topLevelNumbers.totalInvoicesRaised +=
          invoice.totalAmount.value;
        customInvoiceGroups['Active'].topLevelNumbers.totalDues +=
          invoice.dueAmount.value;
        customInvoiceGroups['Active'].topLevelNumbers.totalRaisedInvoices =
          customInvoiceGroups['Active'].topLevelNumbers.totalInvoicesRaised -
          customInvoiceGroups['Active'].topLevelNumbers.totalDues;
      }

      // Calculate top level numbers for Completed group
      if (invoice.status === InvoiceStatus.Paid) {
        customInvoiceGroups['Paid'].topLevelNumbers.totalInvoicesRaised +=
          invoice.totalAmount.value;
        customInvoiceGroups['Paid'].topLevelNumbers.totalRaisedInvoices +=
          invoice.totalAmount.value;
      }
    });

    return Object.values(customInvoiceGroups);
  }

  private async getClientsListInformation(): Promise<Business[]> {
    const [businessSummary, allInvoices] = await Promise.all([
      this.getAllClientBusinesses(),
      this.getAllInvoices([]),
    ]);
    let partners: Business[] = [];
    businessSummary.forEach((partner) => {
      if (partner.partnerId != undefined) {
        partners.push(partner);
      }
    });
    // const allInvoices = await this.getAllInvoices(partners);
    return this.getPartnersWithInvoiceDetails(allInvoices, businessSummary);
  }

  async getBusinessSummary(
    businessSummaryRequest: BusinessSummaryRequest,
  ): Promise<BusinessSummary> {
    let businessSummary = {} as BusinessSummary;
    if (
      businessSummaryRequest.requestType.includes(
        BusinessSummaryRequestType.CLIENT_BUSINESSES,
      )
    ) {
      businessSummary.businesses = await this.getClientsListInformation();
    }

    if (
      businessSummaryRequest.requestType.includes(
        BusinessSummaryRequestType.INVOICE_GROUPS,
      )
    ) {
      const [businesses, sentInvoices] = await Promise.all([
        this.getAllClientBusinesses(),
        this.getSentInvoicesInformation(),
      ]);

      const invoices = await this.convertInvoicesFromApiToFrontend(
        sentInvoices,
        businesses,
      );
      const sentInvoicesGroups =
        this.convertSentInvoicesFromApiToFrontend(invoices);

      businessSummary.businesses = businesses;
      businessSummary.myInvoiceGroups = sentInvoicesGroups;
    }

    if (
      businessSummaryRequest.requestType.includes(
        BusinessSummaryRequestType.TOP_LEVEL_NUMBERS,
      )
    ) {
      businessSummary.topLevelNumbers = await this.getTopLevelNumbers(
        businessSummary.businesses ?? [],
        businessSummary.myInvoiceGroups ?? [],
      );
    }
    return businessSummary;
  }

  private getPartnerVbanExists(partner: PartnerModel): boolean | undefined {
    const partnerWithLegacyFields = partner as PartnerModel & {
      partner_vban_exists?: boolean;
      bankDetailsList?: Array<BankDetails> | null;
    };

    if (typeof partnerWithLegacyFields.partnerVbanExists === 'boolean') {
      return partnerWithLegacyFields.partnerVbanExists;
    }
    if (typeof partnerWithLegacyFields.partner_vban_exists === 'boolean') {
      return partnerWithLegacyFields.partner_vban_exists;
    }
    return undefined;
  }

  private getPartnerBankDetailsList(
    partner: PartnerModel,
  ): Array<BankDetails> | null | undefined {
    const partnerWithLegacyFields = partner as PartnerModel & {
      bankDetailsList?: Array<BankDetails> | null;
    };

    return (
      partnerWithLegacyFields.bank_details_list ??
      partnerWithLegacyFields.bankDetailsList
    );
  }

  private hasPartnerBankDetails(partner: PartnerModel): boolean {
    const bankDetailsList = this.getPartnerBankDetailsList(partner);
    return Array.isArray(bankDetailsList) && bankDetailsList.length > 0;
  }

  private hasNoPartnerBankDetails(partner: PartnerModel): boolean {
    return !this.hasPartnerBankDetails(partner);
  }

  private getPartnerStatus(partner: PartnerModel) {
    switch (partner.status) {
      case 'APPROVED':
        if (this.hasPartnerBankDetails(partner)) {
          return 'approved';
        }
        if (
          this.hasNoPartnerBankDetails(partner) &&
          this.getPartnerVbanExists(partner) !== true
        ) {
          return 'approved';
        }
        return 'verifying';
      case 'REQUESTED':
        return 'requested';
      default:
        return 'rejected';
    }
  }

  async getAllClientBusinesses(): Promise<Business[]> {
    const customer: Profile = this.sharedProfileData.getUserProfile();
    let dataFromPartnersResponse = await this.openapiService.getPartners({
      owning_customer_id: customer.businessId,
    });

    let allPartnerBusinessInfo: Business[] = [];
    dataFromPartnersResponse.data.forEach((partner) => {
      allPartnerBusinessInfo.push(
        this.convertClientDataFromApiToFrontend(partner),
      );
    });
    return allPartnerBusinessInfo;
  }

  private async getAllInvoices(partners: Business[]): Promise<Invoice[]> {
    const customer: Profile = this.sharedProfileData.getUserProfile();
    let partnerIds = partners
      .map((k) => k.partnerId)
      .filter((id) => id !== undefined);
    let dataFromResponse = await this.openapiService.getInvoices(
      customer.businessId,
      {
        partner_ids: partnerIds,
      },
    );
    return this.convertInvoicesFromApiToFrontend(
      dataFromResponse.data,
      partners,
    );
  }

  private getPartnersWithInvoiceDetails(
    invoices: Invoice[],
    businesses: Business[],
  ): Business[] {
    let partners: { [key: string]: Business } = {};

    businesses.forEach((partner) => {
      if (partner.partnerId !== undefined) {
        partners[partner.partnerId] = {
          ...partner,
          active: 0,
          overdue: 0,
          paid: 0,
        };
      }
    });

    invoices.forEach((invoice) => {
      if (invoice.partnerId !== undefined) {
        const id = invoice.partnerId;
        switch (invoice.status) {
          case InvoiceStatus.Unpaid:
            if (partners[id]['active'] !== undefined)
              partners[id]['active'] += 1;
            break;
          case InvoiceStatus.Paid:
            if (partners[id]['paid'] !== undefined) partners[id]['paid'] += 1;
            break;
          case InvoiceStatus.PartialPaid:
            console.log(partners[id]);
            if (partners[id]['active'] !== undefined)
              partners[id]['active'] += 1;
            break;
          case InvoiceStatus.Overdue:
            if (partners[id]['overdue'] !== undefined)
              partners[id]['overdue'] += 1;

            break;
          default:
            break;
        }
      }
    });

    return Object.values(partners);
  }

  async getBeneficialOwners(): Promise<BeneficialOwner[]> {
    const customer: Profile = this.sharedProfileData.getUserProfile();
    const response = await this.openapiService.getBeneficialOwners(
      customer.businessId,
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch beneficial owners');
    }
    return response.data;
  }

  async createBeneficialOwner(request: {
    beneficialOwnerName: string;
    beneficialOwnerPan: string;
    panFileId: string;
    addressFileId?: string;
    ownerAddress?: OwnerAddress;
  }): Promise<BeneficialOwner> {
    const customer: Profile = this.sharedProfileData.getUserProfile();
    const body: PostBeneficialOwnerRequestContent = {
      beneficial_owner_name: request.beneficialOwnerName,
      beneficial_owner_pan: request.beneficialOwnerPan,
      pan_file_id: request.panFileId,
      address_file_id: request.addressFileId,
      owner_address: request.ownerAddress,
    };
    const response = await this.openapiService.postBeneficialOwner(
      customer.businessId,
      body,
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to create beneficial owner');
    }
    return response.data?.data ?? ({} as BeneficialOwner);
  }

  async patchBeneficialOwner(
    ownerId: string,
    panFileId: string,
    options?: {
      ownerAddress?: OwnerAddress;
      addressFileId?: string;
    },
  ): Promise<void> {
    const customer: Profile = this.sharedProfileData.getUserProfile();
    const body: PatchBeneficialOwnerRequestContent = {
      pan_file_id: panFileId,
      address_file_id: options?.addressFileId,
      owner_address: options?.ownerAddress,
    };
    const response = await this.openapiService.patchBeneficialOwner(
      customer.businessId,
      ownerId,
      body,
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to update beneficial owner');
    }
  }

  /**
   * Marks a beneficial owner as no longer a UBO by calling PATCH with
   * operation = REMOVE_UBO. No PAN/address fields are required for this operation.
   */
  async removeUboBeneficialOwner(ownerId: string): Promise<void> {
    const customer: Profile = this.sharedProfileData.getUserProfile();
    const body: PatchBeneficialOwnerRequestContent = {
      pan_file_id: '',
      operation: 'REMOVE_UBO' as PatchOwnerOperation,
    };
    const response = await this.openapiService.patchBeneficialOwner(
      customer.businessId,
      ownerId,
      body,
    );
    if (!response.success) {
      throw new Error(
        response.error || 'Failed to remove UBO status for beneficial owner',
      );
    }
  }

  async deleteBeneficialOwner(ownerId: string): Promise<void> {
    const customer: Profile = this.sharedProfileData.getUserProfile();
    const response = await this.openapiService.deleteBeneficialOwner(
      customer.businessId,
      ownerId,
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete beneficial owner');
    }
  }

  async createCurrencyWallet(
    currency: Currency,
  ): Promise<PostCurrencyWalletResponseContent> {
    const customer: Profile = this.sharedProfileData.getUserProfile();
    const response = await this.openapiService.postCurrencyWallet(
      customer.businessId,
      { currency },
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to create currency wallet');
    }
    return response.data;
  }

  async enableMultiCurrency(
    estimatedMonthlyVolume: Money,
  ): Promise<PostMultiCurrencyEnableResponseContent> {
    const customer: Profile = this.sharedProfileData.getUserProfile();
    const response = await this.openapiService.postMultiCurrencyEnable(
      customer.businessId,
      { estimated_monthly_volume: estimatedMonthlyVolume },
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to enable multi-currency');
    }
    return response.data;
  }

  async getClientBusinessById(clientId: string): Promise<Business> {
    let clientData = await this.openapiService.getPartnerById({
      id: clientId,
    });
    return this.convertClientDataFromApiToFrontend(clientData.data);
  }

  getDueDateFrequency(
    accountDetails: PartnerAccountDetails,
  ): 'Days' | 'Months' {
    let term: 'Days' | 'Months';
    if (accountDetails?.due_date_frequency === 'DAYS') term = 'Days';
    else term = 'Months';
    return term;
  }

  private convertClientDataFromApiToFrontend(
    clientData: PartnerModel,
  ): Business {
    // @todo revoked_date / requested date
    let client: Business = {
      r2AId: '',
      name: clientData.business_name ? clientData.business_name : '',
      emailAddress: clientData.business_email,
      // @todo country name
      country: {
        id: '',
        name: 'usa',
        code: clientData.country_code ? clientData.country_code : '',
      },
      isPartner: true,
      partnerId: clientData.id,
      partnerRelationshipStatus: this.getPartnerStatus(clientData),
      partnerRelationshipRejectionReason: clientData.revoked_reason
        ? clientData.revoked_reason
        : '',
      createdDate: clientData.created_time,
      contactPerson: clientData.contact_person,
      phoneNumber: clientData.contact_person_phone_number,
      currentBalance: {
        value: clientData.wallet_balance?.amount
          ? clientData.wallet_balance?.amount
          : 0,
        currency: clientData.wallet_balance?.currency
          ? clientData.wallet_balance?.currency
          : 'USD',
      },
      bankDetails: this.hasPartnerBankDetails(clientData)
        ? this.convertBankAccountDetailsFromApiToFrontend(
            this.getPartnerBankDetailsList(clientData) ?? [],
          )
        : ([] as PaymentMethodDetails[]),
    };
    const accountDetails = clientData?.partner_account_details;
    if (accountDetails) {
      client.accountDepartment = {
        contactPersonName: accountDetails?.contact_person_name
          ? accountDetails?.contact_person_name
          : '',
        contactPersonEmail: accountDetails?.contact_person_email
          ? accountDetails?.contact_person_email
          : '',
        contactPersonPhNo: accountDetails?.contact_person_ph_no
          ? accountDetails?.contact_person_ph_no
          : '',
        clientCode: accountDetails?.client_code
          ? accountDetails?.client_code
          : '',
        lastInvoiceNumber: accountDetails?.last_invoice_number
          ? accountDetails?.last_invoice_number
          : -1,
        dueDateCount: accountDetails?.due_date_count
          ? accountDetails?.due_date_count
          : 0,
        dueDateFrequency: this.getDueDateFrequency(accountDetails),
      };
    }
    const address = clientData?.address;
    if (address) {
      client.address = {
        street: address?.address_line1 ? address?.address_line1 : '',
        city: address?.city ? address?.city : '',
        state: address?.state ? address?.state : '',
        country: address?.country_code ? address?.country_code : '',
        specificDetails: {
          stateCode: address?.state ? address?.state : '',
          zipCode: address?.pin_code ? address?.pin_code : '',
        },
      };
    }
    return client;
  }

  private convertBankAccountDetailsFromApiToFrontend(
    bankDetailsList: BankDetails[],
  ): PaymentMethodDetails[] {
    let bankDetails: PaymentMethodDetails[] = [];
    let determineRoutingNumber = (bankDetail: BankDetails): string => {
      switch (bankDetail.type) {
        case 'ACH':
          return bankDetail.domestic_credit ?? '';
        case 'RTP':
          return bankDetail.domestic_fast_credit ?? '';
        case 'FEDWIRE':
          return bankDetail.domestic_wire ?? '';
        case 'SWIFT':
          return bankDetail.global_wire ?? '';
      }
      return bankDetail.domestic_credit ?? '';
    };
    bankDetailsList.forEach((bankDetail) => {
      bankDetails.push({
        id: 'id_' + Math.random().toString(16).slice(2),
        accountType: bankDetail.account_type ?? '',
        paymentMethodType: bankDetail.type ?? '',
        accountName: '',
        accountNumber: bankDetail?.number ? bankDetail?.number : '',
        achRouting: determineRoutingNumber(bankDetail),
        bankName: bankDetail?.bank_name ? bankDetail?.bank_name : '',
        bankAddress: '383 Madison Ave, New York, NY 10179, USA',
        ifscCode: 'HDFC0000291',
      });
    });
    return bankDetails;
  }
}
